import type { NormalizedError, SafeLogValue } from './types'

export const REDACTED_VALUE = '[REDACTED]'

const MAX_DEPTH = 5
const MAX_ARRAY_ITEMS = 50
const MAX_OBJECT_KEYS = 100
const MAX_STRING_LENGTH = 4096
const MAX_ERROR_MESSAGE_LENGTH = 2000
const MAX_ERROR_STACK_LENGTH = 16_384

const SENSITIVE_KEYS = new Set([
  'authorization',
  'cookie',
  'setcookie',
  'password',
  'passwd',
  'secret',
  'token',
  'accesstoken',
  'refreshtoken',
  'servertoken',
  'serveraccesstoken',
  'apikey',
  'key',
  'keyhash',
  'xapikey',
  'email',
  'firstname',
  'lastname',
  'customfields',
  'prompt',
  'body',
  'payload',
  'content',
  'html',
  'jsx',
  'raw',
  'instructions',
  'snippet',
  'preview',
  'title',
  'subject',
  'sendername',
  'ip',
  'ipaddress',
  'phone',
  'phonenumber',
  'twilionumber',
  'filename',
  'command',
  'creditcard',
  'cardnumber',
  'cvc',
  'cvv',
  'iban',
])

/**
 * Env vars whose VALUES must never appear in an outbound string. Literal
 * values are scrubbed before the shape-based passes because a secret may be
 * shorter than the generic token threshold. Convex `ArgumentValidationError`
 * echoes whole argument objects (which carry `serverAccessToken` on the
 * Resend webhook path); this is the last line before a third-party index.
 */
const SECRET_ENV_KEYS = [
  'INTERNAL_API_SECRET',
  'DATADOG_API_KEY',
  'RESEND_API_KEY',
  'CONVEX_DEPLOY_KEY',
  'CLERK_SECRET_KEY',
  'STRIPE_SECRET_KEY',
  'MAILGUN_API_KEY',
  'NOTRA_GEO_TOKEN',
] as const

/** The public-API log payload keys blanked by `redactForLogs`. */
const LEGACY_SENSITIVE_KEY_NAMES = new Set([
  'authorization',
  'xbrewplatformadminsession',
  'xapikey',
  'apikey',
  'key',
  'keyhash',
  'email',
  'firstname',
  'lastname',
  'customfields',
])

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-zA-Z]{2,24}\b/g
const TOKEN_PATTERN =
  /\b(?=[A-Za-z0-9_-]{32,}\b)(?=[A-Za-z0-9_-]*[A-Za-z])(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]+\b/g
const SENSITIVE_ASSIGNMENT_PATTERN =
  /\b(authorization|password|passwd|secret|(?:[a-z][\w-]*)?token|api[_-]?key|cookie)\s*[:=]\s*([^\s,;]+)/gi
const ABSOLUTE_URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi
const EMAIL_SEGMENT_PATTERN = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-zA-Z]{2,24}$/
const TOKEN_SEGMENT_PATTERN =
  /^(?=[A-Za-z0-9_-]{32,}$)(?=[A-Za-z0-9_-]*[A-Za-z])(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]+$/
/** One URL-safe run of at most 64 characters: the shape of a correlation id. */
const CANONICAL_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/
const SENSITIVE_ROUTE_PARENTS = new Set([
  'contacts',
  'share',
  'webhook',
  'hooks',
  'tokens',
  'credentials',
])

function normalizeKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

export function isSensitiveLogKey(key: string): boolean {
  const normalized = normalizeKey(key)
  return (
    SENSITIVE_KEYS.has(normalized) ||
    normalized.endsWith('token') ||
    normalized.endsWith('secret') ||
    normalized.endsWith('password') ||
    normalized.endsWith('apikey') ||
    normalized.endsWith('body') ||
    normalized.endsWith('email') ||
    normalized.endsWith('phone') ||
    normalized.endsWith('phonenumber') ||
    normalized.endsWith('prompt') ||
    normalized.endsWith('instructions') ||
    normalized.endsWith('snippet') ||
    normalized.endsWith('preview')
  )
}

export const MAX_MESSAGE_LENGTH = MAX_ERROR_MESSAGE_LENGTH

/**
 * Cap `value` at `maxLength` with an explicit marker, so a cut is never
 * mistaken for the end of the message; the marker counts toward the budget.
 */
export function truncate(value: string, maxLength: number): string {
  return value.length <= maxLength
    ? value
    : `${value.slice(0, Math.max(0, maxLength - 12))}…[truncated]`
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function runtimeEnv(): Record<string, string | undefined> {
  return typeof process === 'undefined' ? {} : process.env
}

const SECRET_PATTERN_CACHE_MAX = 32
const secretPatterns = new Map<string, RegExp>()

/** One compiled pattern per secret value; `replace` resets `lastIndex`. */
function secretPattern(secret: string): RegExp {
  const cached = secretPatterns.get(secret)
  if (cached) {
    return cached
  }
  if (secretPatterns.size >= SECRET_PATTERN_CACHE_MAX) {
    secretPatterns.clear()
  }
  const pattern = new RegExp(escapeRegExp(secret), 'g')
  secretPatterns.set(secret, pattern)
  return pattern
}

/**
 * Replace every occurrence of a configured secret's literal value with
 * `[redacted:<KEY>]`. Values shorter than 8 characters are skipped so a
 * blank or trivial setting cannot redact everything.
 */
export function redactSecretEnvValues(
  value: string,
  env: Record<string, string | undefined> = runtimeEnv()
): string {
  let output = value
  for (const key of SECRET_ENV_KEYS) {
    const secret = env[key]?.trim()
    if (secret && secret.length >= 8) {
      output = output.replace(secretPattern(secret), `[redacted:${key}]`)
    }
  }
  return output
}

function redactNonUrlSensitiveString(value: string): string {
  return redactSecretEnvValues(value)
    .replace(SENSITIVE_ASSIGNMENT_PATTERN, '$1=[REDACTED]')
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(TOKEN_PATTERN, '[redacted-token]')
}

/**
 * Free-form string pass for `error.message` / `error.stack`: literal secret
 * values first (they may be shorter than the token rule), then emails, then
 * token-shaped runs. `env` is injectable for tests.
 */
export function redactSensitive(
  value: string,
  env: Record<string, string | undefined> = runtimeEnv()
): string {
  return redactSecretEnvValues(value, env)
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(TOKEN_PATTERN, '[redacted-token]')
}

function shouldRedactLegacyKey(key: string): boolean {
  return LEGACY_SENSITIVE_KEY_NAMES.has(normalizeKey(key))
}

function redactLegacyValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((entry) => redactLegacyValue(entry))
  }
  if (!value || typeof value !== 'object') {
    return value
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) =>
      shouldRedactLegacyKey(key)
        ? [key, REDACTED_VALUE]
        : [key, redactLegacyValue(entryValue)]
    )
  )
}

/**
 * Structured pass for the public-API log payloads: blank the values under
 * the credential / contact-PII key names, recursively, keeping the payload's
 * shape (`T` in, `T` out). The canonical serializer applies the wider
 * `sanitizeLogValue`; this one exists for the `lib/api/observability`
 * contract its tests pin.
 */
export function redactForLogs<T>({ value }: { value: T }): T {
  return redactLegacyValue(value) as T
}

export function redactSensitiveString(value: string): string {
  return redactNonUrlSensitiveString(value).replace(
    ABSOLUTE_URL_PATTERN,
    (url) => sanitizeUrl(url)
  )
}

function isUrlLogKey(key: string): boolean {
  const normalized = normalizeKey(key)
  return (
    normalized === 'url' ||
    normalized === 'uri' ||
    normalized === 'href' ||
    normalized.endsWith('url') ||
    normalized.endsWith('uri')
  )
}

function stringifyUnknown(value: unknown): string {
  try {
    return String(value)
  } catch {
    try {
      return JSON.stringify(value) ?? '[unserializable]'
    } catch {
      return '[unserializable]'
    }
  }
}

function errorCode(error: Error): string | undefined {
  if (!('code' in error)) {
    return
  }
  const code = (error as Error & { code?: unknown }).code
  return typeof code === 'string' || typeof code === 'number'
    ? String(code)
    : undefined
}

export function normalizeError(
  error: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): NormalizedError {
  if (depth >= 3) {
    return { kind: 'Error', message: '[Cause depth exceeded]' }
  }

  if (!(error instanceof Error)) {
    return {
      kind: typeof error,
      message: truncate(
        redactSensitiveString(stringifyUnknown(error)),
        MAX_ERROR_MESSAGE_LENGTH
      ),
    }
  }

  if (seen.has(error)) {
    return { kind: error.name || 'Error', message: '[Circular error cause]' }
  }
  seen.add(error)

  const causes: Array<NormalizedError> = []
  if (error instanceof AggregateError) {
    for (const member of error.errors.slice(0, 10)) {
      causes.push(normalizeError(member, depth + 1, seen))
    }
  } else if (error.cause !== undefined) {
    causes.push(normalizeError(error.cause, depth + 1, seen))
  }

  return {
    kind: error.name || 'Error',
    message: truncate(
      redactSensitiveString(error.message || error.name || 'Error'),
      MAX_ERROR_MESSAGE_LENGTH
    ),
    ...(error.stack
      ? {
          stack: truncate(
            redactSensitiveString(error.stack),
            MAX_ERROR_STACK_LENGTH
          ),
        }
      : {}),
    ...(errorCode(error) ? { code: errorCode(error) } : {}),
    ...(causes.length > 0 ? { causes } : {}),
  }
}

/** Rebuild an Error after a normalized error crosses a durable boundary. */
export function rehydrateError(normalized: NormalizedError): Error {
  const cause = normalized.causes?.[0]
    ? rehydrateError(normalized.causes[0])
    : undefined
  const error = new Error(normalized.message, cause ? { cause } : undefined)
  error.name = normalized.kind
  if (normalized.stack) {
    error.stack = normalized.stack
  }
  if (normalized.code) {
    Object.assign(error, { code: normalized.code })
  }
  return error
}

/** Keep the originating stack when a durable boundary changes error class. */
export function preserveErrorCause<TError extends Error>(
  error: TError,
  cause: unknown
): TError & { cause: unknown } {
  Object.assign(error, { cause })
  return error as TError & { cause: unknown }
}

/**
 * Where a structured value holds correlation ids, as a tree rooted at that
 * value: `true` marks an id key, an object descends one level. Only these
 * exact paths are ids; the same key anywhere else is an ordinary field.
 */
export type CanonicalIdPaths = {
  readonly [key: string]: CanonicalIdPaths | true
}

function sanitizeValue(
  value: unknown,
  depth: number,
  ancestors: WeakSet<object>,
  idPaths?: CanonicalIdPaths
): SafeLogValue {
  if (value === null || value === undefined) {
    return value === undefined ? '[Undefined]' : null
  }
  if (typeof value === 'string') {
    return truncate(redactSensitiveString(value), MAX_STRING_LENGTH)
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : String(value)
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'bigint') {
    return value.toString()
  }
  if (typeof value === 'symbol' || typeof value === 'function') {
    return `[${typeof value}]`
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? '[Invalid Date]'
      : value.toISOString()
  }
  if (value instanceof URL) {
    return sanitizeUrl(value.toString())
  }
  if (value instanceof Error) {
    return normalizeError(value) as SafeLogValue
  }
  if (depth >= MAX_DEPTH) {
    return '[MaxDepth]'
  }
  if (ancestors.has(value)) {
    return '[Circular]'
  }

  ancestors.add(value)
  try {
    if (Array.isArray(value)) {
      const items = value
        .slice(0, MAX_ARRAY_ITEMS)
        .map((entry) => sanitizeValue(entry, depth + 1, ancestors))
      if (value.length > MAX_ARRAY_ITEMS) {
        items.push(`[${value.length - MAX_ARRAY_ITEMS} more items]`)
      }
      return items
    }

    const output: Record<string, SafeLogValue> = {}
    const entries = Object.entries(value).slice(0, MAX_OBJECT_KEYS)
    for (const [key, entryValue] of entries) {
      const idPath =
        idPaths && Object.hasOwn(idPaths, key) ? idPaths[key] : undefined
      if (isSensitiveLogKey(key)) {
        output[key] = REDACTED_VALUE
      } else if (isUrlLogKey(key) && typeof entryValue === 'string') {
        output[key] = sanitizeUrl(entryValue)
      } else if (
        idPath === true &&
        typeof entryValue === 'string' &&
        CANONICAL_ID_PATTERN.test(entryValue)
      ) {
        // A 32+ character id is exactly what TOKEN_PATTERN scrubs, and no
        // other free-form rule can match this shape; a configured secret's
        // literal value is still removed.
        output[key] = redactSecretEnvValues(entryValue)
      } else {
        output[key] = sanitizeValue(
          entryValue,
          depth + 1,
          ancestors,
          idPath === true ? undefined : idPath
        )
      }
    }
    if (Object.keys(value).length > MAX_OBJECT_KEYS) {
      output._truncated_keys = Object.keys(value).length - MAX_OBJECT_KEYS
    }
    return output
  } finally {
    ancestors.delete(value)
  }
}

/**
 * Recursive structured pass: key-name redaction, URL scrubbing, then the
 * free-form string pass on every remaining string. A string at one of
 * `idPaths` that has the id shape skips the token rule, so the id stays
 * searchable; any other value there takes the full pass.
 */
export function sanitizeLogValue(
  value: unknown,
  idPaths?: CanonicalIdPaths
): SafeLogValue {
  return sanitizeValue(value, 0, new WeakSet(), idPaths)
}

function shouldRedactPathSegment(segment: string, previous: string): boolean {
  if (!segment || /^\[[^\]]+\]$/.test(segment)) {
    return false
  }
  return (
    SENSITIVE_ROUTE_PARENTS.has(previous.toLowerCase()) ||
    EMAIL_SEGMENT_PATTERN.test(segment) ||
    TOKEN_SEGMENT_PATTERN.test(segment)
  )
}

export function sanitizeUrl(value: string): string {
  const isAbsolute = /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(value)
  try {
    const url = new URL(value, 'https://observability.invalid')
    const segments = url.pathname.split('/')
    const sanitizedSegments = segments.map((segment, index) =>
      shouldRedactPathSegment(segment, segments[index - 1] ?? '')
        ? '[redacted]'
        : segment
    )
    const pathname = sanitizedSegments.join('/') || '/'
    if (!isAbsolute) {
      return pathname
    }
    return pathname === '/' ? url.origin : `${url.origin}${pathname}`
  } catch {
    return truncate(
      redactNonUrlSensitiveString(value.split(/[?#]/, 1)[0] ?? ''),
      512
    )
  }
}
